import { Badge, Button, Card, LearnMoreLink, Page, Table, Text, Toggle, Tooltip } from '@ui/themed';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { LimitChildren } from '../../../components/limit-children';
import { SparkSuspense } from '../../../components/spark-suspense';
import { featureRestricted, features, getActiveBillingPlanKey } from '../../../constants/features';
import { useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { useSettings, useUpdateSettings } from '../../../context/settings-context';
import { useCountriesMap } from '../../../hooks/use-countries';
import { useModal } from '../../../hooks/use-modal';
import { DeleteShippingMethodModal } from '../components/modals/delete-shipping-method-modal';
import { ShippingMethodsEmptyState } from '../components/shipping-methods-empty-state';
import { ShippingMethodsUpsell } from '../components/shipping-methods-upsell';
import { useShippingMethods } from '../hooks/use-shipping-methods';
import type { ShippingMethodApiResponse } from '../types/shipping-method';

export const ShippingMethodsPage = withPageProviders(ShippingMethodsPageContent);

function ShippingMethodsPageContent() {
  const { navigationHandler, notificationHandler } = useHandlers();
  const { data: shippingMethods, isLoading, isError, refetch } = useShippingMethods();
  const settings = useSettings();
  const updateSetting = useUpdateSettings();
  const countries = useCountriesMap();
  const env = useEnv();

  const planHasShipping = !featureRestricted(
    features.SHIPPING,
    getActiveBillingPlanKey(env.billing.plan_key),
  );

  const isShippingEnabled = settings.useInternalShipping;

  const [methodToDelete, setMethodToDelete] = useState<ShippingMethodApiResponse | null>(null);
  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal();

  // Get the countries with regions. Countries with restricted regions first
  const countriesWithRegions = useCallback((method: ShippingMethodApiResponse) => {
    const withRegions = method.countries.map((country) => ({
      code: country,
      regions: method.regions[country] ?? [],
    }));

    // Sort by countries with restricted regions first
    // Stable sort, so it will remain alphabetical (as long as API returns alphabetically)
    withRegions.sort(
      ({ regions: r1 }, { regions: r2 }) => (r1.length > 0 ? 0 : 1) - (r2.length > 0 ? 0 : 1),
    );

    return withRegions;
  }, []);

  function setUseInternalShipping(value: boolean) {
    updateSetting.mutate(
      { key: 'useInternalShipping', value },
      {
        onError: () => {
          notificationHandler.error();
        },
      },
    );
  }

  if (!planHasShipping) {
    return (
      <Page
        title={t('features:shipping_methods.pages.shipping_methods_page.header.title')}
        description={t('features:shipping_methods.pages.shipping_methods_page.header.description')}
        learnMoreUrl="https://docs.sparklayer.io/shipping"
        footerDescription={t(
          'features:shipping_methods.pages.shipping_methods_page.footer.description',
        )}
      >
        <ShippingMethodsUpsell />
      </Page>
    );
  }

  return (
    <>
      <Page
        title={t('features:shipping_methods.pages.shipping_methods_page.header.title')}
        description={t('features:shipping_methods.pages.shipping_methods_page.header.description')}
        learnMoreUrl="https://docs.sparklayer.io/shipping"
        footerDescription={t(
          'features:shipping_methods.pages.shipping_methods_page.footer.description',
        )}
        primaryAction={
          isShippingEnabled
            ? {
                icon: 'plusCircle',
                onClick: () => navigationHandler.navigate('/configuration/shipping/create'),
                label: t(
                  'features:shipping_methods.pages.shipping_methods_page.header.actions.create',
                ),
              }
            : undefined
        }
      >
        <Card>
          <Card.Body>
            <Text>
              {t('features:shipping_methods.pages.shipping_methods_page.activation.optional_note')}
            </Text>
            <Text variant="secondary">
              {t('features:shipping_methods.pages.shipping_methods_page.activation.description')}{' '}
              <LearnMoreLink link="https://docs.sparklayer.io/shipping" />
            </Text>

            <span className="flex items-center gap-2">
              <Text>
                {t('features:shipping_methods.pages.shipping_methods_page.activation.enable')}
              </Text>
              <Toggle checked={isShippingEnabled} onChange={setUseInternalShipping} />
            </span>
          </Card.Body>
        </Card>

        {isShippingEnabled && (
          <SparkSuspense
            isError={isError}
            isLoading={isLoading}
            errorMessage={t('errors:generic')}
            onReload={() => void refetch()}
          >
            {shippingMethods.length === 0 ? (
              <ShippingMethodsEmptyState />
            ) : (
              <Table
                headings={[
                  t('features:shipping_methods.pages.shipping_methods_page.table.headings.name'),
                  t(
                    'features:shipping_methods.pages.shipping_methods_page.table.headings.countries',
                  ),
                  t('features:shipping_methods.pages.shipping_methods_page.table.headings.groups'),
                ]}
                caption={t('features:shipping_methods.pages.shipping_methods_page.table.caption')}
              >
                {shippingMethods.map((method, i) => (
                  <Table.Row key={method.id} id={method.id} index={i}>
                    <Table.Cell>
                      <div className="flex flex-col whitespace-nowrap">
                        <Text>{method.title}</Text>
                        <Text size="sm" variant="secondary">
                          {t(
                            'features:shipping_methods.pages.shipping_methods_page.table.row.sku',
                            { sku: method.sku },
                          )}
                        </Text>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <LimitChildren
                        overflowElement={(remaining) => (
                          <Badge key="extra" variant="default">{`+${remaining}`}</Badge>
                        )}
                        limit={6}
                      >
                        {countriesWithRegions(method).map(({ code, regions }) => (
                          <Tooltip
                            key={code}
                            content={t(
                              'features:shipping_methods.pages.shipping_methods_page.table.row.regions_tooltip',
                              {
                                country: countries[code],
                                regions: regions.length,
                              },
                            )}
                          >
                            <Badge variant={regions.length > 0 ? 'orange' : 'blue'}>{code}</Badge>
                          </Tooltip>
                        ))}
                      </LimitChildren>
                    </Table.Cell>
                    <Table.Cell>
                      <LimitChildren
                        overflowElement={(remaining) => <Badge>{`+${remaining}`}</Badge>}
                        limit={3}
                      >
                        {method.groups.map((group) => (
                          <Badge variant="white" key={group}>
                            {group}
                          </Badge>
                        ))}
                      </LimitChildren>
                    </Table.Cell>
                    <Table.Cell sticky>
                      <Button
                        collapsible
                        icon="edit"
                        onClick={() =>
                          navigationHandler.navigate(`/configuration/shipping/edit/${method.id}`)
                        }
                      >
                        {t('buttons:edit')}
                      </Button>
                      <Button
                        collapsible
                        icon="delete"
                        variant="dangerOutline"
                        onClick={() => {
                          setMethodToDelete(method);
                          openDeleteModal();
                        }}
                      >
                        {t('buttons:delete')}
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table>
            )}
          </SparkSuspense>
        )}
      </Page>

      <DeleteShippingMethodModal
        method={methodToDelete}
        isOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
      />
    </>
  );
}
